import React from 'react';

import { Link } from '@gravity-ui/uikit';
import { SmartHomeTestingDate } from 'client/blocks/SmartHomeTestingDate/SmartHomeTestingDate';
import { FetchingHandlerState } from 'client/hooks/useFetchingHandler';
import { i18nWrapper } from 'client/lib/utils';
import { smartHomeDescriptionsAiredKeys, smartHomeDescriptionsKeys } from 'client/locale/ru/skillDescriptions';
import { DevicesTestingStatusMeta } from 'client/model/devicesTestingForm';
import { DraftStatus } from 'client/model/draft';
import { Channel, SmartHome } from 'client/model/skill';
import { getSlaTimings } from 'client/utils/draft-actions';
import * as keyset from 'i18n/DraftControlPanel';

import styles from './SmartHomeModeration.module.scss';

const t = i18nWrapper(keyset);

interface SmartHomeModerationCurrentStatusProps {
    aliceApp: SmartHome;
    status: DraftStatus;
    channel: Channel;
    lastNonCancelledStatus: DraftStatus;
    isMeetingApproval: boolean;
    isTesting: boolean;
    isCancelled: boolean;
    isPrivate: boolean;
    isAllowedForDeploy: boolean;
    cancellationComment?: string;
    draftStatusHint: Record<string, string>;
    fetchingState: FetchingHandlerState;
    loadStatusUnhandled: () => Promise<void>;
    devicesTestingStatusMeta?: DevicesTestingStatusMeta;
    disabled: boolean;
}

export const SmartHomeModerationCurrentStatus: React.FC<SmartHomeModerationCurrentStatusProps> = ({
    aliceApp,
    status,
    channel,
    lastNonCancelledStatus,
    isMeetingApproval,
    isTesting,
    isCancelled,
    isPrivate,
    isAllowedForDeploy,
    cancellationComment,
    draftStatusHint,
    fetchingState,
    loadStatusUnhandled,
    devicesTestingStatusMeta,
    disabled,
}) => {
    const zoomLink = getZoomLink(isTesting, devicesTestingStatusMeta);

    return (
        <>
            {isMeetingApproval && !isPrivate ? (
                <SmartHomeTestingDate
                    aliceApp={aliceApp}
                    state={fetchingState}
                    loadStatusUnhandled={loadStatusUnhandled}
                    devicesTestingStatusMeta={devicesTestingStatusMeta}
                    disabled={disabled}
                />
            ) : !isCancelled ? (
                <>
                    <div className={styles.smartHomeModerationLabel}>{getDescription(aliceApp.onAir, status)}</div>
                    {!isPrivate && !isAllowedForDeploy && draftStatusHint[status] ? (
                        <div className={styles.smartHomeModerationLabel}>{draftStatusHint[status]}</div>
                    ) : null}
                    <div className={styles.smartHomeModerationLabel}>
                        {getSlaTimings('current', channel, lastNonCancelledStatus)}
                    </div>
                </>
            ) : (
                <div className={styles.smartHomeModerationLabel}>{cancellationComment}</div>
            )}
            {zoomLink && (
                <div className={styles.smartHomeModeration__zoomLinkWrapper}>
                    <div className={styles.smartHomeModeration__zoomLink}>{t('meeting-zoom-link')}</div>
                    <Link href={zoomLink} target="_blank">
                        {zoomLink}
                    </Link>
                </div>
            )}
        </>
    );
};

const getDescription = (onAir: boolean, status: DraftStatus) => {
    if (onAir) {
        return t(smartHomeDescriptionsAiredKeys[status]);
    }

    return t(smartHomeDescriptionsKeys[status]);
};

const getZoomLink = (isTesting: boolean, devicesTestingStatusMeta?: DevicesTestingStatusMeta): string | null => {
    if (
        isTesting &&
        devicesTestingStatusMeta &&
        devicesTestingStatusMeta.status !== 'NOT_SPECIFIED' &&
        devicesTestingStatusMeta.record.type === 'zoom' &&
        devicesTestingStatusMeta.record.options.zoomLink
    ) {
        return devicesTestingStatusMeta.record.options.zoomLink;
    }

    return null;
};
